<template>
  <div class="col-sm-12">
    <div class="card target-card" ref="targetCard">
      <div id="header" class="card-header card-no-border target-card-header">
        <div class="target-header-main">
          <div>
            <h5 class="target-title">TARGET VS ACHIEVEMENT</h5>
            <p class="target-subtitle">
              Monthly target and achievement overview
            </p>
          </div>

          <div class="target-actions">
            <button
              type="button"
              class="target-icon-btn"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </button>

            <button
              type="button"
              class="target-icon-btn"
              @click="toggleFullScreen"
              title="Fullscreen"
            >
              <i
                :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"
              ></i>
            </button>
          </div>
        </div>

        <div class="target-filter-bar">
          <select
            v-if="!isDistributor && !isDistributorBackOffice"
            v-model="selectedRole"
            class="form-select target-filter-select"
            @focus="lazyLoadOrgStructures"
            @change="handleRoleChange"
          >
            <option value="">All Roles</option>

            <option
              v-for="orgStruct in orgStructures"
              :key="orgStruct.id"
              :value="orgStruct.id"
            >
              {{ orgStruct.description }}
            </option>
          </select>

          <select
            v-if="isDistributor || isDistributorBackOffice || selectedRole !== ''"
            v-model="selectedName"
            class="form-select target-filter-select target-user-select"
            :disabled="isDistributor || isDistributorBackOffice"
          >
            <option v-if="!isDistributor && !isDistributorBackOffice" :value="null">
              {{
                isDistributorRoleSelected
                  ? "Select Distributor"
                  : userSelectPlaceholder
              }}
            </option>

            <option v-if="!isDistributor && !isDistributorBackOffice && isDistributorRoleSelected" value="">
              All Distributors
            </option>

            <option v-if="isDistributor || isDistributorBackOffice" :value="profileId">
              {{ isDistributorBackOffice ? distributorBackOfficeName : loggedUserName }}
            </option>

            <option
              v-for="user in usersInOrgStructList"
              :key="user.profile_id"
              :value="user.profile_id"
            >
              {{ user.name }}
            </option>
          </select>

          <select
            v-model="selectedYear"
            class="form-select target-filter-select target-year-select"
            @change="handleFilterChange"
          >
            <option v-for="year in yearOptions" :key="year" :value="year">
              {{ year }}
            </option>
          </select>

          <select
            v-model="selectedSku"
            class="form-select target-filter-select target-sku-select"
            @focus="lazyLoadItems"
            @change="handleSkuChange"
          >
            <option value="">All SKUs</option>

            <option v-for="item in items" :key="item.id" :value="item.id">
              {{ item.description }}
            </option>
          </select>

          <select
            v-model="selectedMeasure"
            class="form-select target-filter-select target-measure-select"
            @change="handleMeasureChange"
          >
            <option value="mt" :disabled="isSelectedSkuQtyOnly">
              Volume (MT)
            </option>

            <option value="qty">Quantity (Qty)</option>
          </select>

          <router-link
            :to="{
              name: 'TargetAchievementDetail',
              query: {
                role: selectedRole,
                name: selectedName,
                year: selectedYear,
                sku: selectedSku,
                measure: selectedMeasure,
              },
            }"
            target="_blank"
            class="btn target-view-btn"
          >
            View Details
          </router-link>
        </div>
      </div>

      <div class="card-body target-card-body">
        <!-- Loading spinner -->
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-container">
          <img
            src="../../../../assets/vectors/data_not_found.png"
            alt="Error"
            class="error-image"
          />

          <p class="error-message">{{ error }}</p>
        </div>

        <!-- Chart display -->
        <div v-if="shouldShowChart" class="chart-container">
          <div class="target-axis-caption" style="margin-left: 1rem">
            {{ selectedMeasure === "mt" ? "Volume (MT)" : "Quantity (Qty)" }}
          </div>

          <apexchart
            :key="chartKey"
            :height="chartHeight"
            type="line"
            :options="chartOptions"
            :series="series"
          ></apexchart>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchNextOrgStructList,
  fetchUserListByRole,
  fetchTargetsVsAchievements,
  fetchItemNameList,
} from "@/services/dataService";
import VueApexCharts from "vue3-apexcharts";
import { getApiErrorMessage } from "@/utils/apiError";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  components: {
    apexchart: VueApexCharts,
  },

  data() {
    const userLabel = localStorage.getItem("user_label");
    const profileId = localStorage.getItem("profile_id");

    return {
      currentYearAndMonth: this.getCurrentYearAndMonth(),
      currentYear: new Date().getFullYear(),

      localToken: localStorage.getItem("token_set"),
      profileId,
      isDistributor: userLabel === "DISTRIBUTOR",
      isDistributorBackOffice: userLabel == "DISTRIBUTOR BACK OFFICE",
      loggedUserName: localStorage.getItem("business_name"),
      distributorBackOfficeName: `${localStorage.getItem("reportingL1FirstName") || ""} ${localStorage.getItem("reportingL1LastName") || ""}`.trim(),

      loading: false,
      error: null,

      orgStructures: [],
      orgStructLoaded: false,
      usersInOrgStructList: [],
      items: [],
      itemsLoaded: false,

      selectedRole: "",
      selectedName: (userLabel === "DISTRIBUTOR" || userLabel === "DISTRIBUTOR BACK OFFICE") ? profileId : null,
      selectedYear: new Date().getFullYear(),
      selectedSku: "",
      selectedMeasure: "mt",

      targetsVsAchievements: [],
      series: [],

      isFullScreen: false,
      chartHeight: 305,
      chartKey: 0,

      /*
       * Internal request-safety properties only.
       * They do not change the UI or business functionality.
       */
      componentActive: true,
      targetRequestSequence: 0,
      userRequestSequence: 0,
      orgRequestPromise: null,
      itemRequestPromise: null,
    };
  },

  mounted() {
    document.addEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );

    this.fetchTargetsAndAchievements();
  },

  beforeUnmount() {
    this.componentActive = false;

    /*
     * Invalidate responses that finish after this component
     * has been removed or after a newer request has started.
     */
    this.targetRequestSequence += 1;
    this.userRequestSequence += 1;

    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
  },

  computed: {
    userSelectPlaceholder() {
      if (!this.selectedRole) {
        return "Select User";
      }

      const selected = this.orgStructures.find(
        (org) => String(org.id) === String(this.selectedRole),
      );

      return selected
        ? `Select ${selected.description}`
        : "Select User";
    },

    shouldShowChart() {
      return (
        !this.loading &&
        !this.error &&
        (this.selectedRole === "" ||
          (this.selectedRole !== "" && this.selectedName !== null))
      );
    },

    yearOptions() {
      const years = [];

      for (let year = new Date().getFullYear(); year >= 2000; year--) {
        years.push(year);
      }

      return years;
    },

    isDistributorRoleSelected() {
      const selected = this.orgStructures.find(
        (org) => String(org.id) === String(this.selectedRole),
      );

      return selected?.description?.toUpperCase() === "DISTRIBUTOR";
    },

    selectedSkuItem() {
      if (!this.selectedSku) {
        return null;
      }

      return this.items.find(
        (item) =>
          String(item.id ?? item.variant_id) ===
          String(this.selectedSku),
      );
    },

    isSelectedSkuQtyOnly() {
      if (!this.selectedSku) {
        return false;
      }

      return (
        Number(this.selectedSkuItem?.is_cylinder) === 0 ||
        this.selectedSkuItem?.allowed_measures === "qty"
      );
    },

    chartOptions() {
      const isMT = this.selectedMeasure === "mt";
      const measureSuffix = isMT ? "MT" : "Qty";

      return {
        chart: {
          type: "line",
          height: this.chartHeight,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          parentHeightOffset: 0,
        },

        stroke: {
          curve: "smooth",
          width: 2.5,
        },

        markers: {
          size: 4,
          hover: {
            size: 6,
          },
        },

        dataLabels: {
          enabled: false,
        },

        xaxis: {
          categories: MONTHS,

          title: {
            text: "Month",
            offsetY: 2,

            style: {
              fontSize: "11px",
              fontWeight: 600,
              color: "#64748b",
            },
          },

          labels: {
            style: {
              fontSize: "11px",
              colors: "#64748b",
            },
          },
        },

        yaxis: {
          labels: {
            formatter: (value) =>
              this.formatCompactKAxisValue(value),

            minWidth: 34,
            maxWidth: 46,

            style: {
              fontSize: "11px",
              colors: "#64748b",
            },
          },

          min: 0,
        },

        colors: ["#7064F5", "#54BA4A"],

        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",

          markers: {
            width: 8,
            height: 8,
            radius: 8,
          },
        },

        tooltip: {
          shared: true,
          intersect: false,

          y: {
            formatter: (value) =>
              `${this.formatTruncatedTwoDecimals(
                value,
              )} ${measureSuffix}`,
          },
        },

        grid: {
          show: true,
          borderColor: "#eef2f7",

          padding: {
            top: 8,
            right: 16,
            bottom: 6,
            left: 22,
          },
        },

        responsive: [
          {
            breakpoint: 768,

            options: {
              yaxis: {
                labels: {
                  minWidth: 30,
                  maxWidth: 42,
                },
              },

              grid: {
                padding: {
                  top: 8,
                  right: 10,
                  bottom: 6,
                  left: 18,
                },
              },

              legend: {
                horizontalAlign: "center",
              },
            },
          },
        ],
      };
    },
  },

  watch: {
    selectedName(newValue, oldValue) {
      if (newValue === oldValue || newValue === null) {
        return;
      }

      this.fetchTargetsAndAchievements();
    },
  },

  methods: {
    handleFullscreenChange() {
      const isTargetCardFullscreen =
        document.fullscreenElement === this.$refs.targetCard;

      this.isFullScreen = isTargetCardFullscreen;

      this.$nextTick(() => {
        this.chartHeight = isTargetCardFullscreen
          ? Math.max(300, window.innerHeight - 200)
          : 305;

        this.chartKey += 1;
      });
    },

    formatTruncatedTwoDecimals(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return "0.00";
      }

      const sign = numberValue < 0 ? "-" : "";
      const absoluteValue = Math.abs(numberValue);
      const truncatedValue =
        Math.trunc(absoluteValue * 100) / 100;

      return (
        sign +
        truncatedValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },

    formatCompactKAxisValue(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return "0";
      }

      const sign = numberValue < 0 ? "-" : "";
      const absoluteValue = Math.abs(numberValue);

      if (absoluteValue >= 1000) {
        return `${sign}${Math.trunc(
          absoluteValue / 1000,
        ).toLocaleString("en-US")}K`;
      }

      return `${sign}${this.formatTruncatedTwoDecimals(
        absoluteValue,
      )}`;
    },

    handleRefresh() {
      /*
       * Prevent multiple refresh clicks while the same
       * dashboard request is already loading.
       */
      if (this.loading) {
        return;
      }

      this.fetchTargetsAndAchievements();
    },

    applySkuMeasureRule() {
      if (this.isSelectedSkuQtyOnly) {
        this.selectedMeasure = "qty";
      }
    },

    handleSkuChange() {
      this.applySkuMeasureRule();
      this.handleFilterChange();
    },

    handleMeasureChange() {
      this.applySkuMeasureRule();
      this.updateChartData();
    },

    getCurrentYearAndMonth() {
      const now = new Date();

      return `${now.getFullYear()} ${now.toLocaleString("default", {
        month: "long",
      })}`;
    },

    handleFilterChange() {
      if (
        this.selectedRole === "" ||
        (this.selectedRole !== "" && this.selectedName !== null)
      ) {
        this.fetchTargetsAndAchievements();
      }
    },

    async lazyLoadOrgStructures() {
      if (this.orgStructLoaded) {
        return;
      }

      /*
       * Reuse the same active request when focus fires
       * more than once before the response returns.
       */
      if (this.orgRequestPromise) {
        await this.orgRequestPromise;
        return;
      }

      this.orgRequestPromise = this.fetchOrgStructList();

      try {
        const loaded = await this.orgRequestPromise;

        if (loaded) {
          this.orgStructLoaded = true;
        }
      } finally {
        this.orgRequestPromise = null;
      }
    },

    async lazyLoadItems() {
      if (this.itemsLoaded) {
        return;
      }

      /*
       * Reuse the same active request when the SKU select
       * receives repeated focus events.
       */
      if (this.itemRequestPromise) {
        await this.itemRequestPromise;
        return;
      }

      this.itemRequestPromise = this.loadItems();

      try {
        const loaded = await this.itemRequestPromise;

        if (loaded) {
          this.itemsLoaded = true;
        }
      } finally {
        this.itemRequestPromise = null;
      }
    },

    async loadItems() {
      try {
        const companyId = localStorage.getItem("company_id");

        const response = await fetchItemNameList(
          companyId,
          1,
          this.localToken,
        );

        if (!this.componentActive) {
          return false;
        }

        if (response.data.status === "success") {
          this.items = response.data.data || [];
          this.applySkuMeasureRule();

          return true;
        }

        return false;
      } catch (error) {
        console.error(
          "Item list API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );

        return false;
      }
    },

    updateChartData() {
      const targetByMonth = Array(12).fill(0);
      const achievedByMonth = Array(12).fill(0);
      const isMT = this.selectedMeasure === "mt";

      this.targetsVsAchievements.forEach((item) => {
        const monthIndex = Number(item.month) - 1;

        if (monthIndex >= 0 && monthIndex < 12) {
          targetByMonth[monthIndex] = isMT
            ? item.target_mt ?? 0
            : item.target_qty ?? 0;

          achievedByMonth[monthIndex] = isMT
            ? item.achieved_mt ?? 0
            : item.achieved_qty ?? 0;
        }
      });

      this.series = [
        {
          name: "Target",
          data: targetByMonth,
        },
        {
          name: "Achievement",
          data: achievedByMonth,
        },
      ];
    },

    async fetchOrgStructList() {
      try {
        const response = await fetchNextOrgStructList(
          this.profileId,
          this.localToken,
        );

        if (!this.componentActive) {
          return false;
        }

        this.orgStructures = response.data?.data || [];

        return true;
      } catch (error) {
        console.error(
          "Organisation structure API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );

        return false;
      }
    },

    handleRoleChange() {
      /*
       * Original behaviour preserved:
       * clear the currently selected person and chart.
       */
      this.selectedName = null;
      this.usersInOrgStructList = [];
      this.targetsVsAchievements = [];
      this.series = [];
      this.error = null;

      /*
       * Invalidate a previously running user-list request.
       */
      this.userRequestSequence += 1;

      if (this.selectedRole === "") {
        this.fetchTargetsAndAchievements();
        return;
      }

      this.fetchUsersInOrgStructure();
    },

    async fetchUsersInOrgStructure() {
      const requestId = ++this.userRequestSequence;
      const selectedRoleAtRequestTime = this.selectedRole;

      try {
        /*
         * Original loading/clearing behaviour preserved.
         */
        this.selectedName = null;
        this.error = null;
        this.targetsVsAchievements = [];
        this.series = [];

        const response = await fetchUserListByRole(
          this.profileId,
          selectedRoleAtRequestTime,
          this.localToken,
        );

        /*
         * Ignore an outdated role response if another role
         * was selected while this request was running.
         */
        if (
          !this.componentActive ||
          requestId !== this.userRequestSequence ||
          selectedRoleAtRequestTime !== this.selectedRole
        ) {
          return;
        }

        this.usersInOrgStructList =
          response.data.data || [];
      } catch (error) {
        if (
          !this.componentActive ||
          requestId !== this.userRequestSequence
        ) {
          return;
        }

        console.error(
          "Users by role API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );
      }
    },

    async fetchTargetsAndAchievements() {
      const requestId = ++this.targetRequestSequence;

      try {
        /*
         * Original loading and clearing behaviour preserved.
         */
        this.loading = true;
        this.error = null;
        this.targetsVsAchievements = [];
        this.series = [];

        /*
         * Original payload behaviour preserved.
         */
        const payload = {
          profile_id: this.profileId,
          distributor_id: this.selectedName || "",
          year: this.selectedYear,
          sku_id: this.selectedSku || "",
        };

        const response = await fetchTargetsVsAchievements(
          payload,
          this.localToken,
        );

        /*
         * Ignore an older response when a newer filter
         * request has already started.
         */
        if (
          !this.componentActive ||
          requestId !== this.targetRequestSequence
        ) {
          return;
        }

        if (
          response.data.success === true &&
          response.data.data.length > 0
        ) {
          this.targetsVsAchievements =
            response.data.data;

          this.updateChartData();
        } else {
          this.error =
            response.data.message || "No data available";
        }
      } catch (error) {
        /*
         * Do not display an error from an outdated request.
         */
        if (
          !this.componentActive ||
          requestId !== this.targetRequestSequence
        ) {
          return;
        }

        console.error(
          "Dashboard API failed:",
          error?.response?.status,
          error?.response?.data || error,
        );

        this.error = getApiErrorMessage(error);
      } finally {
        /*
         * An older request must not stop the spinner while
         * the newest request is still running.
         */
        if (
          this.componentActive &&
          requestId === this.targetRequestSequence
        ) {
          this.loading = false;
        }
      }
    },

    async toggleFullScreen() {
      const element = this.$refs.targetCard;

      try {
        if (!document.fullscreenElement) {
          await element?.requestFullscreen?.();
        } else {
          await document.exitFullscreen?.();
        }
      } catch (error) {
        console.error("Fullscreen action failed:", error);
      }
    },
  },
};
</script>

<style scoped>
.target-card {
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.045);
  overflow: hidden;
  background: #ffffff;
}

.target-card-header {
  flex-shrink: 0;
  padding: 12px 14px 9px !important;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
  border-bottom: 1px solid #eef2f7;
}

.target-header-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  min-width: 0;
}

.target-title {
  margin: 0;
  font-size: 14px;
  font-weight: 750;
  line-height: 1.2;
  color: #111827;
  text-transform: none !important;
  letter-spacing: -0.01em;
}

.target-subtitle {
  margin: 3px 0 0;
  font-size: 11px;
  font-weight: 400;
  color: #8a94a6;
  line-height: 1.3;
}

.target-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.target-icon-btn {
  width: 29px;
  height: 29px;
  border: 1px solid #e6eaf0;
  border-radius: 9px;
  background: #ffffff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
}

.target-icon-btn:hover {
  background: #f8fafc;
  color: #111827;
  border-color: #d8dee8;
  transform: translateY(-1px);
}

.target-icon-btn.disabled {
  pointer-events: none;
  opacity: 0.55;
}

/* Filters: wrap naturally, never overlap */
.target-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.target-filter-select {
  flex: 1 1 112px;
  min-width: 0;
  max-width: 180px;
  height: 30px;
  min-height: 30px;
  padding: 4px 26px 4px 9px;
  border: 1px solid #e1e7ef;
  border-radius: 9px;
  background-color: #ffffff;
  color: #1f2937;
  font-size: 11.5px;
  font-weight: 550;
  line-height: 1.2;
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.target-filter-select:focus {
  border-color: #7064f5;
  box-shadow: 0 0 0 3px rgba(112, 100, 245, 0.11);
}

.target-filter-select:disabled {
  background-color: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.target-user-select {
  flex: 1 1 150px;
  max-width: 240px;
}

.target-year-select {
  flex: 0 0 82px;
  max-width: 88px;
}

.target-sku-select {
  flex: 1 1 120px;
  max-width: 190px;
}

.target-measure-select {
  flex: 1 1 115px;
  max-width: 165px;
}

.target-view-btn {
  flex: 0 0 auto;
  height: 30px;
  min-height: 30px;
  padding: 5px 11px;
  border-radius: 9px;
  background: #111827;
  border: 1px solid #111827;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 650;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.target-view-btn:hover {
  background: #000000;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.18);
}

.target-card-body {
  flex: 1;
  min-height: 0;
  padding: 7px 10px 10px !important;
  display: flex;
  flex-direction: column;
}

.chart-container {
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 250px;
  overflow-x: hidden;
  overflow-y: hidden;
}

.chart-container :deep(.apexcharts-canvas),
.chart-container :deep(.apexcharts-svg),
.chart-container :deep(.apexcharts-inner),
.chart-container :deep(foreignObject) {
  max-width: 100% !important;
}

.spinner-container {
  flex: 1;
  min-height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  border: 3px solid rgba(112, 100, 245, 0.12);
  border-top: 3px solid #7064f5;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 0.8s linear infinite;
}

.error-container {
  flex: 1;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.error-image {
  max-width: 120px;
  opacity: 0.9;
}

.error-message {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Fullscreen polish */
.target-card:fullscreen {
  padding: 12px;
  background: #f8fafc;
}

.target-card:fullscreen .target-card-header {
  border-radius: 14px;
}

.target-card:fullscreen .target-card-body {
  background: #ffffff;
  border-radius: 14px;
  margin-top: 10px;
}

.target-card:fullscreen .chart-container {
  min-height: 420px;
}

/* Large / medium screens */
@media (max-width: 1399px) {
  .target-filter-select {
    flex: 1 1 calc(33.333% - 6px);
    max-width: none;
  }

  .target-user-select {
    flex: 1 1 calc(33.333% - 6px);
    max-width: none;
  }

  .target-year-select {
    flex: 0 0 84px;
    max-width: 90px;
  }

  .target-view-btn {
    flex: 1 1 110px;
  }
}

/* Tablet */
@media (max-width: 1199px) {
  .target-card-header {
    padding: 11px 12px 8px !important;
  }

  .target-header-main {
    margin-bottom: 9px;
  }

  .target-filter-select,
  .target-user-select,
  .target-sku-select,
  .target-measure-select,
  .target-view-btn {
    flex: 1 1 calc(33.333% - 6px);
    max-width: none;
  }

  .target-year-select {
    flex: 0 0 84px;
    max-width: 90px;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .target-card-header {
    padding: 10px !important;
  }

  .target-header-main {
    align-items: center;
    margin-bottom: 9px;
  }

  .target-title {
    font-size: 13px;
  }

  .target-subtitle {
    display: none;
  }

  .target-filter-bar {
    gap: 6px;
  }

  .target-filter-select,
  .target-user-select,
  .target-year-select,
  .target-sku-select,
  .target-measure-select,
  .target-view-btn {
    flex: 1 1 calc(50% - 6px);
    max-width: none;
    width: 100%;
    min-width: 0;
    height: 30px;
    min-height: 30px;
    font-size: 11px;
  }

  .target-card-body {
    padding: 6px 8px 9px !important;
  }

  .chart-container {
    min-height: 245px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .chart-container::-webkit-scrollbar {
    height: 4px;
  }

  .chart-container::-webkit-scrollbar-track {
    background: transparent;
  }

  .chart-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }

  .spinner-container,
  .error-container {
    min-height: 245px;
  }
}

/* Small mobile */
@media (max-width: 480px) {
  .target-filter-select,
  .target-user-select,
  .target-year-select,
  .target-sku-select,
  .target-measure-select,
  .target-view-btn {
    flex: 1 1 100%;
  }

  .target-actions {
    gap: 4px;
  }

  .target-icon-btn {
    width: 28px;
    height: 28px;
  }

  .chart-container {
    min-height: 230px;
  }

  .spinner-container,
  .error-container {
    min-height: 230px;
  }
}

.target-retry-btn {
  min-height: 30px;
  padding: 5px 12px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 650;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.target-retry-btn:hover:not(:disabled) {
  border-color: #7064f5;
  color: #7064f5;
  background: #f8f7ff;
}

.target-retry-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.target-refresh-indicator {
  position: absolute;
  top: 7px;
  right: 10px;
  z-index: 5;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  color: #64748b;
  font-size: 10.5px;
  font-weight: 650;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.target-inline-warning {
  position: relative;
  z-index: 5;
  margin: 4px 10px 2px;
  padding: 6px 8px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 10.5px;
  font-weight: 550;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.target-inline-warning button {
  flex-shrink: 0;
  padding: 3px 7px;
  border: 1px solid #fdba74;
  border-radius: 6px;
  background: #ffffff;
  color: #9a3412;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
}

.target-inline-warning button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.chart-container {
  position: relative;
}

.chart-is-refreshing :deep(.apexcharts-canvas) {
  opacity: 0.78;
  transition: opacity 0.2s ease;
}
</style>